import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';

interface BasicInfoSectionProps {
  name: string;
  description: string;
  displayType: string;
  holidays: string[];
  onNameChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  onDisplayTypeChange: (value: string) => void;
  onHolidaysChange: (value: string[]) => void;
}

const BasicInfoSection: React.FC<BasicInfoSectionProps> = ({
  name,
  description,
  displayType,
  holidays,
  onNameChange,
  onDescriptionChange,
  onDisplayTypeChange,
  onHolidaysChange,
}) => {
  const handleHolidayToggle = (value: string) => {
    const newHolidays = holidays.includes(value)
      ? holidays.filter(h => h !== value)
      : [...holidays, value];
    onHolidaysChange(newHolidays);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Basic Information</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label htmlFor="display-name">Display Name *</Label>
          <Input
            id="display-name"
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
            placeholder="Enter your display name"
            required
          />
        </div>

        <div>
          <Label htmlFor="description">Description</Label>
          <Textarea
            id="description"
            value={description}
            onChange={(e) => onDescriptionChange(e.target.value)}
            placeholder="Tell visitors about your display..."
            rows={4}
          />
        </div>

        <div>
          <Label htmlFor="display-type">Display Type</Label>
          <Select value={displayType} onValueChange={onDisplayTypeChange}>
            <SelectTrigger>
              <SelectValue placeholder="Select display type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Residential">Residential</SelectItem>
              <SelectItem value="Commercial">Commercial</SelectItem>
              <SelectItem value="Community">Community</SelectItem>
              <SelectItem value="Church">Church</SelectItem>
              <SelectItem value="School">School</SelectItem>
              <SelectItem value="Other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label>Holidays</Label>
          <div className="flex flex-wrap gap-2 mt-2">
            {['Christmas', 'Halloween', 'FourthOfJuly', 'Easter', 'Thanksgiving', 'Hanukkah'].map((holiday) => (
              <ToggleGroup
                key={holiday}
                type="multiple"
                value={holidays}
                onValueChange={onHolidaysChange}
              >
                <ToggleGroupItem
                  value={holiday}
                  aria-label={`Toggle ${holiday}`}
                  className={`data-[state=on]:bg-primary data-[state=on]:text-primary-foreground ${
                    holidays.includes(holiday) ? 'bg-primary text-primary-foreground' : ''
                  }`}
                  onClick={() => handleHolidayToggle(holiday)}
                >
                  {holiday === 'FourthOfJuly' ? '4th of July' : holiday}
                </ToggleGroupItem>
              </ToggleGroup>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default BasicInfoSection;